<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  filters: { type: Object, required: true },
  sortOrder: { type: String, default: 'none' },
  agents: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  totalCount: { type: Number, default: 0 },
  hasActiveFilters: { type: Boolean, default: false },
});

// Componente apresentacional: nunca escreve na prop `filters` (vue/no-mutating-props);
// devolve as mudanças pelo evento update:filter e o board aplica.
const emit = defineEmits([
  'toggle-sort',
  'reset',
  'add-stage',
  'update:filter',
]);
const { t } = useI18n();

const setFilter = (key, value) => emit('update:filter', { key, value });

// ComboBox exige { value, label } em TODO item; '' é a sentinela de "sem filtro"
const agentOptions = computed(() => [
  { value: '', label: t('KANBAN.FILTERS.AGENT.ALL') },
  { value: 'unassigned', label: t('KANBAN.FILTERS.AGENT.UNASSIGNED') },
  ...props.agents.map(agent => ({
    value: agent.id,
    label: agent.available_name || agent.name,
  })),
]);

const inboxOptions = computed(() => [
  { value: '', label: t('KANBAN.FILTERS.INBOX.ALL') },
  ...props.inboxes.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);

const sortIcon = computed(() => {
  if (props.sortOrder === 'desc') return 'i-lucide-arrow-down-wide-narrow';
  if (props.sortOrder === 'asc') return 'i-lucide-arrow-up-narrow-wide';
  return 'i-lucide-arrow-up-down';
});

const sortLabel = computed(() =>
  t(`KANBAN.SORT.${props.sortOrder.toUpperCase()}`)
);
</script>

<template>
  <header class="flex flex-col gap-3 px-6 py-4 border-b border-n-weak shrink-0">
    <div class="flex items-center gap-3">
      <span class="text-xl i-lucide-square-kanban text-n-brand" />
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('KANBAN.HEADER.TITLE') }}
      </h1>
      <span
        class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11 tabular-nums"
      >
        {{ t('KANBAN.HEADER.COUNT', { count: totalCount }) }}
      </span>
      <Button
        class="ms-auto"
        sm
        icon="i-lucide-plus"
        :label="t('KANBAN.STAGE.ADD.BUTTON')"
        @click="emit('add-stage')"
      />
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Input
        :model-value="filters.search"
        class="w-64"
        :placeholder="t('KANBAN.FILTERS.SEARCH.PLACEHOLDER')"
        @update:model-value="value => setFilter('search', value)"
      />
      <div class="w-52">
        <ComboBox
          :model-value="filters.agentId ?? ''"
          :options="agentOptions"
          :placeholder="t('KANBAN.FILTERS.AGENT.ALL')"
          @update:model-value="
            value => setFilter('agentId', value === '' ? null : value)
          "
        />
      </div>
      <div class="w-52">
        <ComboBox
          :model-value="filters.inboxId ?? ''"
          :options="inboxOptions"
          :placeholder="t('KANBAN.FILTERS.INBOX.ALL')"
          @update:model-value="
            value => setFilter('inboxId', value === '' ? null : value)
          "
        />
      </div>

      <label class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11">
        <Switch
          :model-value="filters.onlyMine"
          @update:model-value="value => setFilter('onlyMine', value)"
        />
        {{ t('KANBAN.FILTERS.ONLY_MINE') }}
      </label>

      <Button
        faded
        slate
        sm
        :icon="sortIcon"
        :label="sortLabel"
        :title="t('KANBAN.SORT.TOOLTIP')"
        @click="emit('toggle-sort')"
      />
      <Button
        v-if="hasActiveFilters"
        ghost
        slate
        sm
        icon="i-lucide-x"
        :label="t('KANBAN.FILTERS.CLEAR')"
        @click="emit('reset')"
      />
    </div>
  </header>
</template>
